// Offline storage service for time tracking data
class OfflineTrackingService {
  constructor() {
    this.STORAGE_KEYS = {
      ACTIVE_SESSION: 'activeTrackingSession',
      PENDING_UPDATES: 'pendingTrackingUpdates',
      OFFLINE_SESSIONS: 'offlineTrackingSessions'
    };
  }

  // Save active session data
  saveActiveSession(sessionData) {
    try {
      const data = {
        ...sessionData,
        lastSaved: Date.now()
      };
      localStorage.setItem(this.STORAGE_KEYS.ACTIVE_SESSION, JSON.stringify(data));
      console.log('💾 Active session saved to offline storage');
    } catch (error) {
      console.error('Failed to save active session:', error);
    }
  }

  // Get active session data
  getActiveSession() {
    try {
      const data = localStorage.getItem(this.STORAGE_KEYS.ACTIVE_SESSION);
      if (data) {
        const parsed = JSON.parse(data);
        // Check if data is not too old (24 hours)
        if (Date.now() - parsed.lastSaved < 24 * 60 * 60 * 1000) {
          return parsed;
        } else {
          this.clearActiveSession();
        }
      }
    } catch (error) {
      console.error('Failed to get active session:', error);
      this.clearActiveSession();
    }
    return null;
  }

  // Clear active session
  clearActiveSession() {
    localStorage.removeItem(this.STORAGE_KEYS.ACTIVE_SESSION);
  }

  // Save offline session (for when starting tracking while offline)
  saveOfflineSession(sessionData) {
    try {
      const sessions = this.getOfflineSessions();
      sessions.push({
        ...sessionData,
        id: `offline_${Date.now()}`,
        createdOffline: true,
        startTime: Date.now()
      });
      localStorage.setItem(this.STORAGE_KEYS.OFFLINE_SESSIONS, JSON.stringify(sessions));
      console.log('💾 Offline session saved');
      return sessions[sessions.length - 1].id;
    } catch (error) {
      console.error('Failed to save offline session:', error);
      return null;
    }
  }

  // Get all offline sessions
  getOfflineSessions() {
    try {
      const data = localStorage.getItem(this.STORAGE_KEYS.OFFLINE_SESSIONS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to get offline sessions:', error);
      return [];
    }
  }

  // Update offline session
  updateOfflineSession(sessionId, updateData) {
    try {
      const sessions = this.getOfflineSessions();
      const sessionIndex = sessions.findIndex(s => s.id === sessionId);
      if (sessionIndex !== -1) {
        sessions[sessionIndex] = {
          ...sessions[sessionIndex],
          ...updateData,
          lastUpdated: Date.now()
        };
        localStorage.setItem(this.STORAGE_KEYS.OFFLINE_SESSIONS, JSON.stringify(sessions));
        console.log('💾 Offline session updated');
      }
    } catch (error) {
      console.error('Failed to update offline session:', error);
    }
  }

  // Remove offline session
  removeOfflineSession(sessionId) {
    try {
      const sessions = this.getOfflineSessions();
      const filtered = sessions.filter(s => s.id !== sessionId);
      localStorage.setItem(this.STORAGE_KEYS.OFFLINE_SESSIONS, JSON.stringify(filtered));
    } catch (error) {
      console.error('Failed to remove offline session:', error);
    }
  }

  // Clear old data (cleanup)
  cleanup() {
    try {
      // Remove offline sessions older than 7 days
      const sessions = this.getOfflineSessions();
      const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      const recent = sessions.filter(s => s.startTime > weekAgo);
      localStorage.setItem(this.STORAGE_KEYS.OFFLINE_SESSIONS, JSON.stringify(recent));
      
      console.log(`🧹 Cleanup completed. Removed ${sessions.length - recent.length} old sessions`);
    } catch (error) {
      console.error('Failed to cleanup offline data:', error);
    }
  }

  // Get storage usage info
  getStorageInfo() {
    try {
      const activeSession = localStorage.getItem(this.STORAGE_KEYS.ACTIVE_SESSION);
      const pendingUpdates = localStorage.getItem(this.STORAGE_KEYS.PENDING_UPDATES);
      const offlineSessions = localStorage.getItem(this.STORAGE_KEYS.OFFLINE_SESSIONS);
      
      return {
        activeSessionSize: activeSession ? activeSession.length : 0,
        pendingUpdatesSize: pendingUpdates ? pendingUpdates.length : 0,
        offlineSessionsSize: offlineSessions ? offlineSessions.length : 0,
        totalSize: (activeSession?.length || 0) + (pendingUpdates?.length || 0) + (offlineSessions?.length || 0)
      };
    } catch (error) {
      console.error('Failed to get storage info:', error);
      return { totalSize: 0 };
    }
  }

  // Export all offline data (for debugging)
  exportData() {
    return {
      activeSession: this.getActiveSession(),
      offlineSessions: this.getOfflineSessions(),
      storageInfo: this.getStorageInfo()
    };
  }
}

export default new OfflineTrackingService();